import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditBalance } from '@/components/practitioner/CreditBalance';
import { CreditPurchasePanel } from '@/components/user/CreditPurchasePanel';
import { SubscriptionCard } from '@/components/practitioner/SubscriptionCard';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { ArrowLeft, Clock, CreditCard, TrendingUp, TrendingDown, Crown, Coins } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';

export const PractitionerCreditsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'credits';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { credits, transactions, isLoading } = usePractitionerCredits();

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== defaultTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, defaultTab, setSearchParams]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted rounded w-96"></div>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-48 bg-muted rounded"></div>
            <div className="h-48 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Link to="/practitioner">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Dashboard
                </Button>
              </Link>
            </div>
            <h1 className="text-3xl font-bold">Credits & Billing</h1>
            <p className="text-muted-foreground">
              Manage your credits, subscription, and billing
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="credits" className="flex items-center gap-2">
              <Coins className="h-4 w-4" />
              Credits
            </TabsTrigger>
            <TabsTrigger value="subscription" className="flex items-center gap-2">
              <Crown className="h-4 w-4" />
              Subscription
            </TabsTrigger>
          </TabsList>

          <TabsContent value="credits" className="space-y-6">
        {/* Credit Balance */}
        <div className="grid gap-6 md:grid-cols-2">
          <CreditBalance showTransactions={false} />
          
          {credits && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Usage Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-muted/20 rounded-lg">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-600" />
                      <div className="text-lg font-bold">{credits.total_credits_purchased}</div>
                      <p className="text-xs text-muted-foreground">Total Purchased</p>
                    </div>
                    <div className="text-center p-3 bg-muted/20 rounded-lg">
                      <TrendingDown className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                      <div className="text-lg font-bold">{credits.total_credits_used}</div>
                      <p className="text-xs text-muted-foreground">Total Used</p>
                    </div>
                  </div>
                  
                  <div className="text-center p-3 bg-primary/5 rounded-lg border">
                    <CreditCard className="h-5 w-5 mx-auto mb-1 text-primary" />
                    <div className="text-lg font-bold text-primary">{credits.credits_balance}</div>
                    <p className="text-xs text-muted-foreground">Current Balance</p>
                  </div>

                  {credits.total_credits_purchased > 0 && (
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        Usage Efficiency: {Math.round((credits.total_credits_used / credits.total_credits_purchased) * 100)}%
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm">Credit transactions will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant={
                            transaction.transaction_type === 'purchase' ? 'default' :
                            transaction.transaction_type === 'deduction' ? 'secondary' :
                            transaction.transaction_type === 'refund' ? 'outline' :
                            'secondary'
                          }
                        >
                          {transaction.transaction_type}
                        </Badge>
                        <span className={`font-medium ${
                          transaction.credits_amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.credits_amount > 0 ? '+' : ''}{transaction.credits_amount} credits
                        </span>
                      </div>
                      
                      {transaction.notes && (
                        <p className="text-sm text-muted-foreground mb-1">{transaction.notes}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{format(new Date(transaction.created_at), 'MMM d, yyyy h:mm a')}</span>
                        {transaction.session_id && (
                          <span>Session: {transaction.session_id.slice(0, 8)}...</span>
                        )}
                        {transaction.stripe_payment_intent_id && (
                          <span>Payment: {transaction.stripe_payment_intent_id.slice(-4)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Purchase Section */}
        <CreditPurchasePanel />
          </TabsContent>

          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionCard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};